import { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useSocketIo } from '../hooks/useSocketIo';

const LogsContext = createContext(null);

const TOPIC = '/tsmart-nbiot-broker';
const HARD_CAP = 500; // tope absoluto en memoria

export const LogsProvider = ({ children }) => {
  const { result, status, subscribe, unSubscribe } = useSocketIo();
  const isSubscribed = useRef(false);

  const [logQueue, setLogQueue] = useState([]);
  const [paused, setPaused] = useState(false);
  const pausedBufferRef = useRef([]);

  // Suscripción única
  useEffect(() => {
    if (!isSubscribed.current && status === 'connected') {
      subscribe([TOPIC]);
      isSubscribed.current = true;
    }
  }, [status, subscribe]);

  useEffect(() => () => {
    if (isSubscribed.current) {
      unSubscribe([TOPIC]);
      isSubscribed.current = false;
    }
  }, [unSubscribe]);

  // Entrada de logs
  useEffect(() => {
    const rawLog = result?.result?.msg;
    if (!rawLog) return;

    let parsed = rawLog;
    try {
      parsed = JSON.parse(rawLog);
    } catch (_) { }

    const newItem = { topic: result?.result?.topic, content: parsed };

    if (paused) {
      // En pausa: buffer (sin pérdidas)
      pausedBufferRef.current.push(newItem);
    } else {
      // En vivo: añadir respetando maxLogs
      setLogQueue(prev => {
        const merged = [...prev, newItem];
        return merged.length > HARD_CAP ? merged.slice(-HARD_CAP) : merged;
      });
    }
  }, [result?.result?.msg, paused, HARD_CAP, result?.result?.topic]);

  // Al reanudar, volcar buffer de forma atómica
  useEffect(() => {
    if (!paused && pausedBufferRef.current.length) {
      const buffered = pausedBufferRef.current.splice(0, pausedBufferRef.current.length); // move all
      setLogQueue(prev => {
        const merged = [...prev, ...buffered];
        return merged.length > HARD_CAP ? merged.slice(-HARD_CAP) : merged;
      });
    }
  }, [paused, HARD_CAP]);

  const handleClear = useCallback(() => {
    setLogQueue([]);
    pausedBufferRef.current = [];
  }, []);

  const togglePause = useCallback(() => setPaused(p => !p), []);

  const value = useMemo(() => ({
    logs: logQueue,
    paused,
    clear: handleClear,
    togglePause,
    setPaused,
    connectionStatus: status,
  }), [logQueue, paused, handleClear, togglePause, status]);

  return <LogsContext.Provider value={value}>{children}</LogsContext.Provider>;
};

export const useLogs = () => {
  const ctx = useContext(LogsContext);
  if (!ctx) throw new Error('useLogs debe usarse dentro de <LogsProvider>');
  return ctx;
};